import * as React from "react";
import {inject, observer} from "mobx-react";
import styled from "styled-components";
import {computed} from "mobx";
import {Permission, XyiconFeature} from "../../../generated/api/base";
import type {DocumentModel} from "../../../data/models/DocumentModel";
import {isOpenableInNewTab} from "../../../data/models/FileType";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {ToggleContainerV5} from "../widgets/ToggleContainerV5/ToggleContainerV5";
import {SearchFieldV5} from "../input/search/SearchFieldV5";
import {IconButtonV5} from "../interaction/IconButtonV5";
import type {AppState} from "../../../data/state/AppState";
import type {TransportLayer} from "../../../data/TransportLayer";
import type {IModel} from "../../../data/models/Model";
import {FileUtils} from "../../../utils/file/FileUtils";
import type {IDropdownOption} from "../interaction/DropdownOptionsV5";
import {baseDistance, FlexCenter, VerticalFlex} from "../styles/styles";
import DownloadIcon from "../icons/download.svg?react";
import DeleteIcon from "../icons/delete.svg?react";
import OpenInNewTabIcon from "../icons/arrow-up-right-from-square.svg?react";
import uploadIcon from "../icons/upload.svg?react";
import TileViewIcon from "../icons/table-cells-large.svg?react";
import CardViewIcon from "../icons/table-list.svg?react";
import {colorPalette} from "../styles/colorPalette";
import {LoaderV5} from "../loader/LoaderV5";
import {ArrayUtils} from "../../../utils/data/array/ArrayUtils";
import {WebCamV5} from "../../../utils/image/WebCamV5";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import {DocumentViewV5} from "./DocumentViewV5";

interface IDocumentSectionProps {
	readonly items: IModel[];
	readonly feature: XyiconFeature;
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
	readonly saveStateToLocalStorage?: boolean;
}

interface IDocumentSectionState {
	search: string;
	listView: boolean;
	isWebCamOpen: boolean;
}

export interface DocumentSectionItem {
	document: DocumentModel;
	inherited: boolean;
	allowDelete: boolean;
}

@inject("appState")
@inject("transport")
@observer
export class DocumentSectionV5 extends React.Component<IDocumentSectionProps, IDocumentSectionState> {
	private static readonly allowedFeatures = [
		XyiconFeature.Portfolio,
		XyiconFeature.Space,
		XyiconFeature.XyiconCatalog,
		XyiconFeature.Xyicon,
		XyiconFeature.Boundary,
	];

	private _documentItems: DocumentSectionItem[] = [];

	constructor(props: IDocumentSectionProps) {
		super(props);
		this.state = {
			search: "",
			listView: this.readListView(),
			isWebCamOpen: false,
		};
	}

	@computed
	public get uploadArray() {
		return this.props.appState.app.transport.services.document.uploadArray;
	}

	private readListView() {
		const {appState, transport} = this.props;
		const organizationId = appState.organizationIdByAuthData;

		if (organizationId) {
			return Boolean(transport.services.localStorage.get(this.getKey(organizationId)));
		}
		return false;
	}

	private saveListView(value: boolean) {
		const {appState, transport} = this.props;
		const organizationId = appState.organizationIdByAuthData;

		if (organizationId) {
			transport.services.localStorage.set(this.getKey(organizationId), value);
		}
	}

	private getKey(organizationId: string = "") {
		return `srv4-org-${organizationId}-documentsection-listview`;
	}

	private get documentPermissions() {
		const {appState, items, feature} = this.props;

		if (feature === XyiconFeature.Portfolio) {
			return appState.actions.getPortfolioPermission?.(items[0]?.id);
		} else if (feature === XyiconFeature.XyiconCatalog) {
			return appState.user?.getOrganizationPermission?.(XyiconFeature.XyiconCatalog);
		} else {
			const typeId = appState.actions.getTypeById(items[0]?.typeId)?.id;
			return appState.actions.getModuleTypePermission?.(typeId, feature, items[0]?.portfolioId);
		}
	}

	private getOptions = (docItem: DocumentSectionItem): IDropdownOption[] => {
		const doc = docItem.document;
		const isOpenableInNewTabSupported = isOpenableInNewTab(doc.fileExtension);

		return [
			isOpenableInNewTabSupported && {
				label: "Open in a new tab",
				onClick: () => this.openInNewTab(doc),
				IconComponent: OpenInNewTabIcon,
			},
			{
				label: "Download",
				onClick: () => this.onDownloadClick(doc),
				IconComponent: DownloadIcon,
			},
			docItem.allowDelete && {
				label: "Delete",
				onClick: () => this.onDeleteClick(doc),
				IconComponent: DeleteIcon,
			},
		].filter((option) => !!option);
	};

	private openInNewTab = (doc: DocumentModel) => {
		window.open(doc.fileUrl);
	};

	private onDownloadClick = (doc: DocumentModel) => {
		this.props.transport.services.document.download(doc);
	};

	private onDeleteClick = async (doc: DocumentModel) => {
		await this.props.transport.services.document.deleteDocument(doc, this.props.feature);
	};

	private uploadFileList = (fileList: FileList) => {
		const files: File[] = [];

		for (let i = 0; i < fileList.length; ++i) {
			files.push(fileList[i]);
		}

		this.uploadFiles(files);
	};

	private uploadFiles = (files: File[]) => {
		const {items, transport} = this.props;
		const array = [...this.uploadArray];
		const element = array.find((el) => el.id === items[0].id);

		if (element) {
			element.count += files.length;
		} else {
			array.push({id: items[0].id, count: files.length});
		}

		ArrayUtils.replaceObservable(this.uploadArray, array);

		for (const file of files) {
			transport.services.document.createDocument(file, items[0]).then(() => {
				array[array.findIndex((i) => i.id === items[0].id)].count -= 1;
				const newArray = [...array];

				ArrayUtils.replaceObservable(this.uploadArray, newArray);
			});
		}
	};

	private onAddClick = () => {
		FileUtils.openFileDialogue(true, null, this.uploadFileList);
	};

	private onWebCamClose = () => {
		this.setState({isWebCamOpen: false});
	};

	private onWebCamClick = () => {
		this.setState({isWebCamOpen: true});
	};

	private handleWebCamResult = (result: {base64: string; fileName: string; extension: string}) => {
		if (result.base64) {
			const file = FileUtils.dataURL2File(
				result.base64,
				`${result.fileName ?? new Date().toLocaleString().replaceAll(", ", "_")}.${result.extension}`,
			);
			this.uploadFiles([file]);
		}

		this.setState({isWebCamOpen: false});
	};

	private onSearchChange = (value: string) => {
		this.setState({search: value});
	};

	private onListViewClicked = () => {
		this.setState({listView: true});
		this.saveListView(true);
	};

	private onCardViewClicked = () => {
		this.setState({listView: false});
		this.saveListView(false);
	};

	private onKeyUp = (event: KeyboardEvent) => {
		if (event.key === KeyboardListener.KEY_ESCAPE) {
			this.onWebCamClose();
		}
	};

	public override componentWillUnmount() {
		KeyboardListener.getInstance().signals.up.remove(this.onKeyUp);
	}

	public override componentDidMount() {
		KeyboardListener.getInstance().signals.up.add(this.onKeyUp);
		const portfolioIdMaybe =
			this.props.items[0]?.ownFeature === XyiconFeature.Portfolio
				? this.props.items[0]?.id
				: (this.props.items[0]?.portfolioId ?? this.props.appState.activePortfolioId);
		if (portfolioIdMaybe) {
			this.props.transport.services.feature.refreshList(XyiconFeature.PortfolioDocument, false, portfolioIdMaybe);
		}
		this.props.transport.services.feature.refreshList(XyiconFeature.OrganizationDocument);
	}

	private getDocuments() {
		const {items, appState} = this.props;
		const {search} = this.state;

		// TODO optimize in case there'll be lots of documents
		const results: DocumentSectionItem[] = [];

		const allDocuments = [
			...appState.actions.getList<DocumentModel>(XyiconFeature.PortfolioDocument),
			...appState.actions.getList<DocumentModel>(XyiconFeature.OrganizationDocument),
		];

		allDocuments.forEach((doc) => {
			if (search) {
				if (!StringUtils.containsIgnoreCase(doc.fileName, search)) {
					return;
				}
			}

			let inherited = false;
			let isAttached = false;

			let usageCount = 0;

			for (const item of items) {
				if (item.ownFeature === XyiconFeature.Xyicon) {
					if (doc.isAttachedToObject(item.catalogId)) {
						inherited = true;
						usageCount++;
					}
				}

				if (doc.isAttachedToObject(item.id)) {
					isAttached = true;
					usageCount++;
				}
			}

			if (isAttached) {
				inherited = false;
			}

			if (isAttached || inherited) {
				results.push({
					document: doc,
					// We only allow deleting a document if it belong to one of the selected items and
					// it doesn't belong to more than one of the selected items
					inherited: inherited,
					allowDelete: !inherited && usageCount < 2 && this.documentPermissions > Permission.View,
				});
			}
		});

		return results;
	}

	public override render() {
		const {items, feature, appState} = this.props;
		const {listView, search, isWebCamOpen} = this.state;
		const isLoading = !appState.lists[feature].loaded;
		const {uploadArray} = this;
		const count = uploadArray.find((i) => i.id === items[0].id)?.count || 0;
		const onAddClickMaybe = items.length === 1 && this.documentPermissions > Permission.View ? this.onAddClick : null;
		const onWebClickedMaybe = items.length === 1 && this.documentPermissions > Permission.View ? this.onWebCamClick : null;

		if (!DocumentSectionV5.allowedFeatures.includes(feature)) {
			return null;
		}

		this._documentItems = this.getDocuments();

		return (
			<>
				<ToggleContainerV5
					title="Documents"
					onAddClick={onAddClickMaybe}
					onWebCamClick={onWebClickedMaybe}
					saveStateToLocalStorage={this.props.saveStateToLocalStorage}
					customOnAddIcon={uploadIcon}
					onAddButtonTitle="Upload"
				>
					<DocumentSectionStyled>
						{!isLoading ? (
							<VerticalFlex $gap={baseDistance.md}>
								<FlexCenter $gap={baseDistance.sm}>
									<SearchFieldV5
										value={search}
										placeholder="Find ..."
										onInput={this.onSearchChange}
									/>
									<IconButtonV5
										IconComponent={CardViewIcon}
										isActive={listView}
										onClick={this.onListViewClicked}
										className="layout-icon"
									/>
									<IconButtonV5
										IconComponent={TileViewIcon}
										isActive={!listView}
										onClick={this.onCardViewClicked}
										className="layout-icon"
									/>
								</FlexCenter>
								<DocumentViewV5
									documentItems={this._documentItems}
									getOptions={this.getOptions}
									uploadingArraySize={count}
									type={listView ? "card" : "tile"}
								/>
							</VerticalFlex>
						) : (
							<LoaderV5 label="Loading Documents..." />
						)}
					</DocumentSectionStyled>
				</ToggleContainerV5>
				{isWebCamOpen && (
					<WebCamV5
						onClose={this.onWebCamClose}
						onComplete={this.handleWebCamResult}
					/>
				)}
			</>
		);
	}
}

const DocumentSectionStyled = styled.div`
	.layout-icon {
		&.isActive {
			background-color: ${colorPalette.primary.c200Light};

			svg path {
				stroke: ${colorPalette.primary.c500Primary};
			}
		}
	}
`;
